import { execSync } from "child_process"
import { existsSync } from "fs"
import { join } from "path"
import {
  CLIError,
  type FrameworkConfig,
  type ProjectConfig,
} from "../types/types.js"
import { logger } from "../utils/logger.js"

export abstract class BaseFramework {
  protected config: FrameworkConfig

  protected constructor(config: FrameworkConfig) {
    this.config = config
  }

  abstract getStarterCommand(projectConfig: ProjectConfig): string
  abstract getPostInstallTasks(
    projectConfig: ProjectConfig,
  ): Array<() => Promise<void>>

  async scaffold(projectConfig: ProjectConfig): Promise<void> {
    const command = this.getStarterCommand(projectConfig)

    if (projectConfig.dryRun) {
      logger.debug(`[DRY RUN] Would run: ${command}`)
      return
    }

    logger.info(`Creating ${this.config.displayName} project...`)
    logger.debug(`Running: ${command}`)

    try {
      execSync(command, {
        cwd: process.cwd(),
        stdio: projectConfig.debug ? "inherit" : "ignore",
      })
      logger.success(`${this.config.displayName} project created`)

      process.chdir(projectConfig.projectPath)
      logger.debug(`Changed working directory to: ${projectConfig.projectPath}`)
    } catch (error) {
      throw new CLIError(
        `Failed to create ${this.config.displayName} project`,
        "SCAFFOLD_FAILED",
        error,
      )
    }

    const tasks = this.getPostInstallTasks(projectConfig)
    for (const task of tasks) {
      await task()
    }
  }

  protected async updatePackageJson(
    projectConfig: ProjectConfig,
    updates: Record<string, any>,
  ): Promise<void> {
    if (projectConfig.dryRun) {
      logger.debug("[DRY RUN] Would update package.json")
      return
    }

    const packageJsonPath = join(projectConfig.projectPath, "package.json")

    try {
      const { readFileSync, writeFileSync } = await import("fs")

      if (!existsSync(packageJsonPath)) {
        logger.debug(
          `package.json not found at ${packageJsonPath}, skipping update`,
        )
        return
      }

      const packageJson = JSON.parse(readFileSync(packageJsonPath, "utf-8"))

      for (const [key, value] of Object.entries(updates)) {
        if (
          typeof value === "object" &&
          !Array.isArray(value) &&
          packageJson[key]
        ) {
          packageJson[key] = { ...packageJson[key], ...value }
        } else {
          packageJson[key] = value
        }
      }

      writeFileSync(packageJsonPath, JSON.stringify(packageJson, null, 2))
      logger.debug("Updated package.json")
    } catch (error) {
      throw new CLIError(
        "Failed to update package.json",
        "UPDATE_PACKAGE_JSON_FAILED",
        error,
      )
    }
  }

  getName(): string {
    return this.config.name
  }

  getDisplayName(): string {
    return this.config.displayName
  }

  getMetaFrameworks(): Array<string> {
    return this.config.metaFrameworks
  }

  hasMetaFramework(meta: string): boolean {
    return this.config.metaFrameworks.includes(meta as any)
  }
}
